import { useSyncExternalStore } from 'react';
import { library, IconDefinition } from '@fortawesome/fontawesome-svg-core';
import iconsUrl from './icons.json?url';

type IconTuple = IconDefinition['icon'];
type IconData = Record<string, Record<string, IconTuple>>;

let pending: Promise<void> | undefined;
let loaded = false;

const listeners = new Set<() => void>();

function settle() {
  loaded = true;
  listeners.forEach((notify) => notify());
}

/**
 * Fetches the name-addressable icons and registers them with FontAwesome.
 *
 * These are the ones picked by a string in the data — jss_sections.icon today — so the
 * browser cannot know at build time which it will need. The tree used to import all of
 * them defensively: 61KB gzipped, 31% of the bundle. They now travel as their own
 * hashed asset, which changes far less often than application code and so survives
 * deploys in the browser cache.
 *
 * Icons known statically — the phone, the cart, the account menu — are still ordinary
 * imports. Those tree-shake properly and none of this applies to them.
 *
 * Nothing waits on this. No name-addressed icon renders on first paint: the desktop
 * panels sit inside a Popover.Panel and the mobile menu inside a <Transition show>,
 * both of which HeadlessUI leaves unmounted until opened. The fetch starts as early as
 * it can and lands long before anyone opens a menu — and NamedIcon draws a placeholder
 * and redraws for anyone quick enough to beat it.
 */
export function loadIcons(): Promise<void> {
  if (!pending) {
    pending = fetch(iconsUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }

        return response.json() as Promise<IconData>;
      })
      .then((data) => {
        for (const [prefix, icons] of Object.entries(data)) {
          for (const [iconName, icon] of Object.entries(icons)) {
            library.add({ prefix, iconName, icon } as IconDefinition);
          }
        }
      })
      .catch((error) => {
        // Icons keep their placeholder rather than the page failing.
        console.error('Could not load the V2 icons', error);
      })
      .finally(settle);
  }

  return pending;
}

function subscribe(notify: () => void) {
  listeners.add(notify);

  return () => {
    listeners.delete(notify);
  };
}

/** Re-renders the caller once the icons have been registered. */
export function useIconsLoaded(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => loaded,
    () => true // server-rendered markup is built with the icons already in hand
  );
}
